import { create } from "zustand";
import { Operation } from "@/backend/models/operation";
import { Code } from "@/backend/models/code";
import { CodeParams, CodeRunResultReply, CodeStatus, PrepareCodeRunReply } from "@/types/codeTypes";
import { I18n } from "@/i18n/I18n";
import { CodeController } from "../controllers/codeController";
import { nativeBridge } from "../embed/nativeBridge";
import { useParameterStore } from "./ParameterStore";
import { useTreeStore } from "./TreeStore";

/**
 * Runs a code operation.
 *
 * Three processes, as a commanding run has: this app owns the record, the
 * desktop app runs it, and a Python process on the user's machine does the
 * work. A run is
 *
 *   prepare here -> code.run over the bridge -> desktop saves the result and
 *   answers with what this app made of it
 *
 * Desktop saves it, not this page, so a run carries on being recorded when the
 * page is left. There is nothing to poll: the runner answers once.
 */

interface CodeRunStore {
  /** Keyed by operation, so a row can ask about its own run. */
  runningByOperationId: Record<string, boolean>;
  runErrorByOperationId: Record<string, string>;

  runCode: (operation: Operation) => Promise<void>;
  reset: () => void;
}

const initialState = {
  runningByOperationId: {} as Record<string, boolean>,
  runErrorByOperationId: {} as Record<string, string>,
};

export const useCodeRunStore = create<CodeRunStore>((set, get) => {
  const end = (operationId: string, error = '') => {
    set(state => {
      const running = { ...state.runningByOperationId };
      delete running[operationId];
      return {
        runningByOperationId: running,
        runErrorByOperationId: { ...state.runErrorByOperationId, [operationId]: error },
      };
    });
  };

  /**
   * Puts the code back, for a run that never reached the runner.
   *
   * Written through the API rather than only into the store: `prepare` marked
   * the row as running on the server, so a row left that way shows a spinner
   * on every later load of the page, and suppresses the stale-input warning
   * `CodeItemRun` raises for a code whose inputs changed since it last ran.
   *
   * The reason goes to stderr as well, so a reload still says why rather than
   * showing a run that simply failed.
   */
  const markNotRunning = async (operation: Operation, prepared: CodeParams, message: string) => {
    const failed = { ...prepared, returnCode: CodeStatus.FAILURE, stderr: message };
    show(operation, { code: failed });

    try {
      await new CodeController().updateCode({ code: failed });
    } catch {
      // The run has already failed and the page says so. A write that fails
      // too leaves the row pending, which the next run overwrites.
    }
  };

  /**
   * A code row as the server wrote it. Checked rather than trusted: the one
   * that comes back from a run was relayed by another process, and `Code`
   * fills in whatever is missing -- `returnCode` becomes 0, the script empty.
   * Half a row shown is a run that reads as having finished and printed
   * nothing, which is worse than one that never appears.
   */
  const isCode = (code: unknown): code is CodeParams =>
    !!code
    && typeof (code as CodeParams).id === 'string'
    && typeof (code as CodeParams).returnCode === 'number';

  /**
   * What desktop said went wrong, however it said it: `{ error: 'why' }` from
   * the bridge's own failures, `{ error: { message } }` from the main process.
   */
  const errorIn = (reply: unknown): string => {
    const error = (reply as { error?: unknown })?.error;
    if (typeof error === 'string') return error;
    const message = (error as { message?: unknown })?.message;
    return typeof message === 'string' ? message : '';
  };

  const show = (operation: Operation, reply: { code?: unknown }) => {
    if (!isCode(reply.code)) return;
    useTreeStore.getState().setOperation(new Operation({ ...operation, code: reply.code }));
  };

  return {
    ...initialState,

    runCode: async (operation: Operation) => {
      const operationId = operation.id;
      if (get().runningByOperationId[operationId]) return;

      set(state => ({
        runningByOperationId: { ...state.runningByOperationId, [operationId]: true },
        runErrorByOperationId: { ...state.runErrorByOperationId, [operationId]: '' },
      }));

      let prepared: PrepareCodeRunReply;
      try {
        prepared = await new CodeController().prepareRun((operation.code as Code).id);
      } catch (error) {
        // `Client` throws, rather than returning an error, when it has no token
        // to build headers with.
        end(operationId, (error as Error).message);
        return;
      }

      if (prepared.status !== 201) {
        end(operationId, prepared.errors?.[0]?.message || I18n.get('codeRunFailed'));
        return;
      }

      // Shows it as running, from what the server stored rather than a guess.
      show(operation, prepared);

      try {
        const reply = await nativeBridge.call<{ result?: CodeRunResultReply; error?: string }>(
          'code.run',
          { codeId: (operation.code as Code).id, runRequest: prepared.runRequest },
        );

        // A reply with no result is a run that did not happen. Desktop answers
        // that way when the main process threw -- the runner is not up, the
        // save failed -- because its IPC wrapper turns a throw into a 500 it
        // resolves with rather than a rejection. Without this the button
        // simply stops spinning and says nothing.
        if (!reply?.result) {
          throw new Error(errorIn(reply) || I18n.get('codeRunFailed'));
        }

        show(operation, reply.result);
        // The outputs the run wrote, and whatever consumes them. Checked
        // rather than trusted: this came from another process, over a bridge
        // that relays what it is given without looking inside it.
        const { updatedParameters, updatedReferences } = reply.result;
        if (Array.isArray(updatedParameters) && Array.isArray(updatedReferences)) {
          useParameterStore.getState().handleUpdatedParamsAndRefs(reply.result);
        }

        end(operationId);
      } catch (error) {
        // The run never reached the runner, or desktop could not save it. The
        // code was marked as running when it was prepared, so say it is not.
        // `prepared.code` rather than the operation's, which is the row as it
        // was before this run marked it.
        await markNotRunning(operation, prepared.code, (error as Error).message);
        end(operationId, (error as Error).message);
      }
    },

    reset: () => set({ ...initialState }),
  };
});
